import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

const ITEMS = [
  ['Customer and site details', 'Record the customer contact, installation address and preferred way to follow up. Confirm that the quote covers the correct site and contact.'],
  ['Fence scope and measurements', 'Break the fence into measured runs. Describe the material, style and height for each run, and identify any areas excluded from the estimate.'],
  ['Gates and hardware', 'List the number and width of gates, opening direction and requested hardware. Keep gate quantities separate from fence footage so they are easy to review.'],
  ['Removal and site conditions', 'Record existing fence removal, disposal, access restrictions, slope and other observed conditions that may affect the work. Identify unknowns that need a site visit.'],
  ['Pricing and assumptions', 'Check footage, gate and tear-out quantities against your rates. State what the price includes, what remains unconfirmed and how scope changes will be discussed.'],
  ['Customer review and next step', 'Review the scope with the customer, confirm how they can ask questions and choose a follow-up date. Keep the accepted scope available for the production handoff.'],
];

const STORAGE_KEY = 'picketops:estimate-review:v1';

function readProgress() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(saved)
      ? [...new Set(saved.filter(index => Number.isInteger(index) && index >= 0 && index < ITEMS.length))]
      : [];
  } catch {
    return [];
  }
}

export default function EstimateChecklist() {
  const { isAuthenticated } = useAuth();
  const [checked, setChecked] = useState(readProgress);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [remainingOnly, setRemainingOnly] = useState(false);
  const [resetSnapshot, setResetSnapshot] = useState(null);
  const [shareStatus, setShareStatus] = useState('');
  const [notes, setNotes] = useState({});
  const [copyingReview, setCopyingReview] = useState(false);
  const [reviewCopyStatus, setReviewCopyStatus] = useState('');

  useEffect(() => {
    setReviewCopyStatus('');
  }, [checked, notes]);
  const noteCount = Object.values(notes).filter(value => value.trim()).length;
  const hasNotes = noteCount > 0;
  const publicUrl = 'https://picketops.apper.so/fence-estimate-checklist';

  const resetReview = () => {
    setResetSnapshot({ checked: [...checked], notes: { ...notes } });
    setChecked([]);
    setNotes({});
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setShareStatus('Link copied. Your checklist progress is not included.');
    } catch {
      setShareStatus('Copy the public link below. Your checklist progress is not included.');
    }
  };

  useEffect(() => {
    try {
      if (checked.length) {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
      setStorageAvailable(true);
    } catch {
      setStorageAvailable(false);
    }
  }, [checked]);

  const nextUnchecked = ITEMS.findIndex((_, index) => !checked.includes(index));
  const goToNext = () => {
    const target = document.getElementById('estimate-check-' + nextUnchecked);
    target?.scrollIntoView({ block: 'center', behavior: 'auto' });
    target?.focus({ preventScroll: true });
  };

  const reviewText = [
    'PicketOps — Fence estimate checklist',
    checked.length + ' of ' + ITEMS.length + ' reviewed',
    '',
    ...ITEMS.flatMap(([title, detail], index) => [
      (checked.includes(index) ? '[x] ' : '[ ] ') + title,
      detail,
      ...(notes[index]?.trim() ? ['Review note: ' + notes[index].trim()] : []),
      '',
    ]),
    'Review complete does not mean the estimate has been approved.',
    'Confirm open questions with your customer before sending the final scope.',
    'https://picketops.apper.so/fence-estimate-checklist',
  ].join('\n');

  const copyReview = async () => {
    if (copyingReview) return;
    setCopyingReview(true);
    try {
      await navigator.clipboard.writeText(reviewText);
      setReviewCopyStatus('Review copied, including all six items, checkmarks and notes. Paste it into your own records.');
    } catch {
      setReviewCopyStatus('Copy is unavailable in this browser. Use Download review to keep the full checklist and notes.');
    } finally {
      setCopyingReview(false);
    }
  };

  return (
    <div className="min-h-svh bg-background text-foreground">
      <a href="#review-heading" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-lg focus:bg-primary focus:px-4 focus:py-3 focus:text-primary-foreground">Skip to estimate review</a>
      <header className="max-w-4xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" className="font-heading text-xl font-bold text-primary hover:underline">PICKETOPS</Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link>
          <span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">Fence estimate checklist</span>
        </nav>
      </header>
      <main className="max-w-4xl mx-auto px-4 pb-12 space-y-8">
        <section className="space-y-4 rounded-2xl border border-primary/20 bg-primary/5 p-6 sm:p-8">
          <p className="text-xs uppercase tracking-widest font-semibold text-primary">Free contractor resource · No signup needed</p>
          <h1 className="text-3xl md:text-4xl font-heading font-semibold">Fence estimate checklist</h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            A useful fence quote explains the scope as clearly as the price. Use this six-part review before sharing an estimate, then carry the agreed details into your work order.
          </p>
          <p className="text-sm text-muted-foreground">
            Check items as you review them. Progress is kept in this browser tab through reloads when browser storage is available. Add optional notes for open questions. Notes stay only in this open page and are lost on reload or when you leave. Download your review to keep them, then reset before reviewing another estimate.
          </p>
        </section>
        <nav aria-label="On this checklist" className="rounded-xl border border-border bg-card p-4 sm:p-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">On this page</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              ['review-heading', 'Start reviewing', 'Six scope checks'],
              ['review-summary-heading', 'Review summary', 'Notes, copy and download'],
              ['share-heading', 'Share the resource', 'A clean public link'],
              ['questions-heading', 'Common questions', 'How to use the checklist'],
            ].map(([id, title, detail]) => (
              <a key={id} href={'#' + id} className="rounded-lg border border-border bg-background p-3 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="block text-sm font-semibold">{title}</span>
                <span className="mt-1 block text-xs text-muted-foreground">{detail}</span>
              </a>
            ))}
          </div>
        </nav>
        <section aria-labelledby="review-heading" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="review-heading" tabIndex={-1} className="text-xl font-heading font-semibold scroll-mt-4">Before you send the quote</h2>
            <p role="status" className="text-sm font-medium tabular-nums">{checked.length} of {ITEMS.length} reviewed</p>
          </div>
          <div className="sticky top-3 z-20 rounded-xl border border-primary/20 bg-background p-4 sm:p-5 space-y-3 shadow-md">
            <div
              role="progressbar"
              aria-label="Estimate review progress"
              aria-valuemin={0}
              aria-valuemax={ITEMS.length}
              aria-valuenow={checked.length}
              aria-valuetext={checked.length + ' of ' + ITEMS.length + ' items reviewed'}
              className="h-2 overflow-hidden rounded-full bg-muted"
            >
              <div className="h-full bg-primary" style={{ width: (checked.length / ITEMS.length * 100) + '%' }} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {nextUnchecked === -1 ? 'All six areas reviewed. Download your copy below.' : 'Next to review: ' + ITEMS[nextUnchecked][0]}
              </p>
              <div className="flex flex-wrap gap-2">
                {nextUnchecked !== -1 && <Button variant="outline" size="sm" onClick={goToNext}>Go to next item</Button>}
                <Button variant="outline" size="sm" asChild><a href="#review-summary-heading">View summary</a></Button>
                <Button asChild size="sm">
                  <a href={'data:text/plain;charset=utf-8,' + encodeURIComponent(reviewText)} download="picketops-estimate-review.txt">Download review</a>
                </Button>
              </div>
            </div>
            {hasNotes && <p className="text-xs text-muted-foreground">{noteCount} {noteCount === 1 ? 'note' : 'notes'} in this review · Download before leaving to keep your notes.</p>}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4">
            <div className="flex items-center gap-3">
              <Checkbox id="remaining-only" checked={remainingOnly} onCheckedChange={value => setRemainingOnly(value === true)} />
              <label htmlFor="remaining-only" className="cursor-pointer text-sm font-medium py-2">Show remaining only</label>
            </div>
            <p className="text-sm text-muted-foreground" role="status">
              {remainingOnly ? ITEMS.length - checked.length : ITEMS.length} items shown
            </p>
          </div>
          {remainingOnly && checked.length === ITEMS.length && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
              <h3 className="font-semibold">All review items are checked</h3>
              <p className="mt-2 text-sm text-muted-foreground">Show all items to review your selections, or download the full checklist below. Customer approval is still a separate step.</p>
              <Button variant="outline" className="mt-4" onClick={() => setRemainingOnly(false)}>Show all items</Button>
            </div>
          )}
          {ITEMS.map(([title, detail], index) => (!remainingOnly || !checked.includes(index)) && (
            <Card key={title} className={checked.includes(index) ? 'border-primary/40 bg-primary/5' : ''}>
              <CardContent className="p-5 flex gap-4 items-start">
                <Checkbox
                  id={'estimate-check-' + index}
                  className="mt-1"
                  checked={checked.includes(index)}
                  onCheckedChange={value => {
                    setResetSnapshot(null);
                    setChecked(current => value === true
                      ? [...new Set([...current, index])]
                      : current.filter(item => item !== index));
                    if (remainingOnly && value === true) {
                      requestAnimationFrame(() => document.getElementById('review-heading')?.focus({ preventScroll: true }));
                    }
                  }}
                  aria-describedby={'estimate-detail-' + index}
                />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-semibold">
                    <label htmlFor={'estimate-check-' + index} className="cursor-pointer block py-1"><span aria-hidden="true" className="text-primary mr-2 tabular-nums">{String(index + 1).padStart(2, '0')}</span>{title}</label>
                  </h3>
                  <p id={'estimate-detail-' + index} className="mt-2 text-sm text-muted-foreground leading-relaxed">{detail}</p>
                  <details className="mt-4 rounded-lg border border-border bg-background p-3">
                    <summary className="cursor-pointer text-sm font-medium rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                      {notes[index]?.trim() ? 'Review note added' : 'Add a review note'}
                    </summary>
                    <label htmlFor={'estimate-note-' + index} className="block mt-3 mb-2 text-sm font-medium">
                      Note for {title.toLowerCase()}
                    </label>
                    <Textarea
                      id={'estimate-note-' + index}
                      value={notes[index] || ''}
                      maxLength={1000}
                      rows={3}
                      aria-describedby={'estimate-note-help-' + index}
                      placeholder="What needs to be confirmed before sending the quote?"
                      onChange={event => {
                        setResetSnapshot(null);
                        setNotes(current => ({ ...current, [index]: event.target.value }));
                      }}
                    />
                    <p id={'estimate-note-help-' + index} className="mt-2 text-xs text-muted-foreground">
                      {(notes[index] || '').length}/1000 characters · Included in your download. Not saved after leaving or reloading.
                    </p>
                  </details>
                </div>
              </CardContent>
            </Card>
          ))}
          {!storageAvailable && (
            <p role="status" className="text-sm text-muted-foreground">
              This browser cannot save progress. Keep this page open or download your review before leaving.
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <Button asChild>
              <a href={'data:text/plain;charset=utf-8,' + encodeURIComponent(reviewText)} download="picketops-estimate-review.txt">
                Download review
              </a>
            </Button>
            <Button variant="outline" disabled={checked.length === 0 && !hasNotes} onClick={resetReview}>
              Reset checklist
            </Button>
          </div>
          {resetSnapshot !== null && (
            <div className="flex flex-wrap items-center gap-3 rounded-xl border bg-muted/30 p-4">
              <p role="status" className="text-sm">Checklist and notes reset. Restore your previous {resetSnapshot.checked.length} reviewed items and notes if needed.</p>
              <Button variant="outline" size="sm" onClick={() => {
                setChecked(resetSnapshot.checked);
                setNotes(resetSnapshot.notes);
                setResetSnapshot(null);
              }}>Undo reset</Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Only checkmarks persist through reloads in this tab. Notes are kept in memory until you leave or reload; download them first. This checklist does not create an estimate or send your notes to PicketOps.
          </p>
          {checked.length === ITEMS.length && (
            <p className="text-sm text-primary">Review complete. Confirm any open questions with your customer before sending the final scope.</p>
          )}
        </section>
        <section aria-labelledby="review-summary-heading" className="overflow-hidden rounded-2xl border border-primary/20 bg-card">
          <div className="border-b border-border bg-primary/5 p-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">Bring the details together</p>
            <h2 id="review-summary-heading" tabIndex={-1} className="mt-2 text-2xl font-heading font-semibold scroll-mt-6">Your review summary</h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Review your notes in one place before you leave. Checking an area does not resolve its notes or record customer approval.
            </p>
            <dl className="mt-5 grid grid-cols-3 gap-3">
              {[
                ['Reviewed', checked.length],
                ['Remaining', ITEMS.length - checked.length],
                ['With notes', noteCount],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-border bg-background p-3">
                  <dt className="text-xs text-muted-foreground">{label}</dt>
                  <dd className="mt-1 text-2xl font-bold tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="p-6 space-y-5">
            {hasNotes ? (
              <ul className="space-y-3" aria-label="Review notes summary">
                {ITEMS.map(([title], index) => notes[index]?.trim() && (
                  <li key={title} className="rounded-xl border border-border bg-background p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className="text-sm font-semibold">{title}</h3>
                      <span className="text-xs text-muted-foreground">{checked.includes(index) ? 'Area reviewed' : 'Area not reviewed'}</span>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm text-muted-foreground">{notes[index].trim()}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No notes added. Use “Add a review note” under any checklist item to record details you still need to confirm.</p>
            )}
            <div className="flex flex-wrap gap-3">
              <Button onClick={copyReview} disabled={copyingReview}>{copyingReview ? 'Copying…' : 'Copy review text'}</Button>
              <Button asChild variant="outline">
                <a href={'data:text/plain;charset=utf-8,' + encodeURIComponent(reviewText)} download="picketops-estimate-review.txt">Download review</a>
              </Button>
            </div>
            <p role="status" className="text-sm text-muted-foreground">{reviewCopyStatus}</p>
            <p className="text-xs text-muted-foreground">Copy and download include every item, even when filtered. Notes stay only in this open page. Nothing is sent to a customer or saved to a PicketOps job.</p>
          </div>
        </section>
        <section aria-labelledby="share-heading" className="rounded-2xl border bg-card p-6 space-y-3">
          <h2 id="share-heading" tabIndex={-1} className="text-xl font-heading font-semibold scroll-mt-6">Share the checklist with your team</h2>
          <p className="text-sm text-muted-foreground">Send the free resource so someone else can start their own review. The public link never includes your selections or notes.</p>
          <Button variant="outline" onClick={copyPublicLink}>Copy public checklist link</Button>
          <p role="status" className="text-sm text-muted-foreground">{shareStatus}</p>
          {shareStatus.startsWith('Copy the') && (
            <a className="block break-all text-sm text-primary underline" href={publicUrl}>{publicUrl}</a>
          )}
        </section>
        <section className="space-y-3">
          <h2 className="text-xl font-heading font-semibold">Turn the estimate into a clear crew handoff</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Keep the accepted fence measurements, gate specifications and scope notes together. When scheduling the install, add the crew assignment, material requirements and any access notes so the crew can work from the same information the customer reviewed.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            PicketOps connects fence leads, estimates, proposals, follow-up records and production planning in one contractor workspace.
          </p>
        </section>
        <section aria-labelledby="questions-heading" className="space-y-4">
          <h2 id="questions-heading" tabIndex={-1} className="text-xl font-heading font-semibold scroll-mt-6">Fence quote questions, answered</h2>
          <div className="divide-y rounded-xl border px-5">
            {[
              ['What should a fence estimate include?', 'Include the installation address, measured fence runs, material, height, gates and hardware, removal, site conditions and pricing assumptions. Make exclusions and unresolved questions visible so the customer can review the scope before accepting it.'],
              ['How should I organize fence measurements?', 'Record each run separately, with its length, material, height and any changes in direction or site conditions. Keep gates and removal quantities separate. A single total can hide differences that affect materials and labor.'],
              ['Does checking every box approve the estimate?', 'No. This is a review aid. It does not calculate a price, confirm site measurements, record customer acceptance or update a work order. Resolve outstanding questions with the customer and keep the accepted scope with the job.'],
              ['Can I reuse this checklist for another job?', 'Download the current review if you want to keep it, then choose Reset checklist. Progress belongs to this browser tab and is not linked to a customer or job. Your download includes every review item, its checked state and any notes, even when Show remaining only is enabled. Notes do not survive leaving or reloading the page.'],
            ].map(([question, answer]) => (
              <details key={question} className="py-4">
                <summary className="cursor-pointer font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary rounded">{question}</summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{answer}</p>
              </details>
            ))}
          </div>
        </section>
        <Card className="border-l-4 border-l-primary">
          <CardContent className="p-6 space-y-4">
            <h2 className="text-xl font-heading font-semibold">Keep the next quote organized in PicketOps</h2>
            <p className="text-sm text-muted-foreground">Start with a 30-day free trial. Create your company profile, then request operator activation. Support confirms the trial and $49/month founding plan terms. Signup does not start billing.</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to={isAuthenticated ? '/estimates' : '/contractor/signup'}>
                  {isAuthenticated ? 'Open your estimates' : 'Create contractor account'}
                </Link>
              </Button>
              <Button variant="outline" asChild><Link to="/#features">Explore PicketOps features</Link></Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export const route = { path: '/fence-estimate-checklist', layout: 'public' };
